import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLink,
  faPencil,
  faTrashAlt,
} from "@fortawesome/pro-regular-svg-icons";
import { Popover, Tooltip } from "../../common";
import {
  AnalyticsSavedFilterDto,
  parseSavedFilterDocument,
} from "../../../types/analytics/AnalyticsSavedFilter";

interface AnalyticsSavedFiltersMenuProps {
  isOpen: boolean;
  onOpenChange(open: boolean): void;
  isDirty: boolean;
  items: AnalyticsSavedFilterDto[];
  appliedSavedFilterId: number | null;
  canPinPageDefault: boolean;
  onSaveCurrent(): void;
  onRecall(item: AnalyticsSavedFilterDto): void;
  /** Clear the selected saved filter so the band label returns to "Filters". */
  onClearSavedSelection(): void;
  onRename(item: AnalyticsSavedFilterDto): void;
  onShare(item: AnalyticsSavedFilterDto): void;
  onDelete(item: AnalyticsSavedFilterDto): void;
  onSetPageDefault(item: AnalyticsSavedFilterDto): void;
  onClearPageDefault(): void;
  /** Tag saves that carry a Show past data date. Off where point-in-time is unusable. */
  showPointInTimeTag?: boolean;
  /** Caller owns the trigger (Filters label + caret on the filter band). */
  triggerElement: ReactNode;
}

function AnalyticsSavedFiltersMenu({
  isOpen,
  onOpenChange,
  isDirty,
  items,
  appliedSavedFilterId,
  canPinPageDefault,
  onSaveCurrent,
  onRecall,
  onClearSavedSelection,
  onRename,
  onShare,
  onDelete,
  onSetPageDefault,
  onClearPageDefault,
  showPointInTimeTag = false,
  triggerElement,
}: AnalyticsSavedFiltersMenuProps) {
  const { t } = useTranslation();

  const savedFilterHasPointInTime = (item: AnalyticsSavedFilterDto) => {
    if (!showPointInTimeTag) return false;
    const doc = parseSavedFilterDocument(item.filterJson);
    return (doc?.pointInTimeDate?.trim() ?? "") !== "";
  };

  return (
    <Popover
      openState={isOpen}
      onOpenStateChange={onOpenChange}
      showCloseIcon={false}
      placement="bottom"
      contentClassNames="w-80 bg-white p-0 border border-gray-200 shadow-lg-around"
      triggerElement={triggerElement}
    >
      <div className="text-left">
        {isDirty && (
          <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 border-b border-gray-200 text-sm">
            <span className="flex-1 text-gray-700">
              {t(
                "Analytics.Filters.Saved.KeepThese",
                "Keep these filters?"
              )}
            </span>
            <button
              type="button"
              className="btn-primary !px-3 !py-1 text-xs rounded-md"
              onClick={() => {
                onSaveCurrent();
                onOpenChange(false);
              }}
            >
              {t("Common.Save", "Save")}
            </button>
          </div>
        )}

        <div className="max-h-72 overflow-y-auto">
          <button
            type="button"
            className="w-full px-3 py-2.5 text-left border-b border-gray-100 hover:bg-gray-50"
            onClick={() => {
              onClearSavedSelection();
              onOpenChange(false);
            }}
          >
            <div className="font-semibold text-sm text-gray-800">
              {t("Analytics.Filters.Saved.NoSelection", "No saved filter")}
              {appliedSavedFilterId == null && (
                <span className="ml-1 text-xs font-normal text-green-600">
                  ✓
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500">
              {t(
                "Analytics.Filters.Saved.NoSelectionHint",
                "Clear the filters"
              )}
            </div>
          </button>
          {items.length === 0 && (
            <div className="px-3 py-4 text-sm text-gray-500">
              {t(
                "Analytics.Filters.Saved.Empty",
                "No saved filters for this page yet."
              )}
            </div>
          )}
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-start gap-2 px-3 py-2.5 border-b border-gray-100 hover:bg-gray-50"
            >
              <button
                type="button"
                className="flex-1 text-left min-w-0"
                onClick={() => {
                  onRecall(item);
                  onOpenChange(false);
                }}
              >
                <div className="font-semibold text-sm text-gray-800 truncate">
                  {item.name}
                  {appliedSavedFilterId === item.id && (
                    <span className="ml-1 text-xs font-normal text-green-600">
                      ✓
                    </span>
                  )}
                </div>
                <div className="mt-0.5">
                  <Tooltip
                    content={
                      item.visibility === "SHARED"
                        ? t(
                            "Analytics.Filters.Saved.SharedTooltip",
                            "Anyone at your company can use this"
                          )
                        : t(
                            "Analytics.Filters.Saved.PrivateTooltip",
                            "Only you can see this"
                          )
                    }
                    triggerElement={
                      <span
                        className={
                          item.visibility === "SHARED"
                            ? "text-[10px] font-medium uppercase tracking-wide bg-primary/20 text-gray-800 px-1.5 py-0.5 rounded"
                            : "text-[10px] font-medium uppercase tracking-wide bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded"
                        }
                      >
                        {item.visibility === "SHARED"
                          ? t("Analytics.Filters.Saved.Shared", "Shared")
                          : t("Analytics.Filters.Saved.Private", "Private")}
                      </span>
                    }
                  />
                </div>
              </button>
              <div className="flex flex-col items-end gap-1 shrink-0">
                {item.isPageDefault && (
                  <span className="text-[10px] font-bold uppercase tracking-wide bg-primary/20 text-gray-800 px-1.5 py-0.5 rounded">
                    {t(
                      "Analytics.Filters.Saved.OpensByDefault",
                      "Opens by default"
                    )}
                  </span>
                )}
                {savedFilterHasPointInTime(item) && (
                  <Tooltip
                    content={t(
                      "Analytics.Filters.Saved.PastDataTooltip",
                      "This saved filter opens with a data snapshot, not today.",
                    )}
                    triggerElement={
                      <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded">
                        {t("Analytics.Filters.Saved.PastData", "Snapshot")}
                      </span>
                    }
                  />
                )}
                {canPinPageDefault &&
                  item.visibility === "SHARED" &&
                  !item.isPageDefault && (
                    <button
                      type="button"
                      className="text-xs text-gray-500 underline"
                      onClick={() => onSetPageDefault(item)}
                    >
                      {t(
                        "Analytics.Filters.Saved.SetDefault",
                        "Set as page default"
                      )}
                    </button>
                  )}
                {canPinPageDefault && item.isPageDefault && (
                  <button
                    type="button"
                    className="text-xs text-gray-500 underline"
                    onClick={() => onClearPageDefault()}
                  >
                    {t(
                      "Analytics.Filters.Saved.ClearDefault",
                      "Clear page default"
                    )}
                  </button>
                )}
                <div className="flex items-center gap-0.5">
                  {item.canEdit && (
                    <Tooltip
                      content={t(
                        "Analytics.Filters.Saved.RenameTooltip",
                        "Rename this saved filter"
                      )}
                      triggerElement={
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                          aria-label={t(
                            "Analytics.Filters.Saved.RenameTooltip",
                            "Rename this saved filter"
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRename(item);
                          }}
                        >
                          <FontAwesomeIcon icon={faPencil} className="text-xs" />
                        </button>
                      }
                    />
                  )}
                  {item.visibility === "SHARED" && (
                    <Tooltip
                      content={t(
                        "Analytics.Filters.Saved.ShareTooltip",
                        "Copy share link"
                      )}
                      triggerElement={
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                          aria-label={t(
                            "Analytics.Filters.Saved.ShareTooltip",
                            "Copy share link"
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            onShare(item);
                          }}
                        >
                          <FontAwesomeIcon icon={faLink} className="text-xs" />
                        </button>
                      }
                    />
                  )}
                  {item.canEdit && (
                    <Tooltip
                      content={t("Common.Delete", "Delete")}
                      triggerElement={
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-red-600 hover:bg-red-50"
                          aria-label={t("Common.Delete", "Delete")}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(item);
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faTrashAlt}
                            className="text-xs"
                          />
                        </button>
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-4 px-3 py-2.5 border-t border-gray-200">
          <button
            type="button"
            className="text-xs font-semibold text-gray-600 underline"
            onClick={() => {
              onSaveCurrent();
              onOpenChange(false);
            }}
          >
            {t(
              "Analytics.Filters.Saved.SaveCurrent",
              "Save current filters…"
            )}
          </button>
        </div>
      </div>
    </Popover>
  );
}

export default AnalyticsSavedFiltersMenu;
